/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperBasketsV2, ShopperProducts } from '@/scapi';
import {
    isCheckoutBlockedCartItem,
    isHardUnavailableCartItem,
    isUnavailableCartItem,
} from '@/lib/cart/unavailable-item';
import { basketHasHomeDeliveryItem } from '@/lib/checkout/delivery-group-display';

export function getIsCheckoutDisabled(params: {
    basket: ShopperBasketsV2.schemas['Basket'] | null | undefined;
    merchandiseItems: ShopperBasketsV2.schemas['ProductItem'][];
    productsByItemIdForAvailability?: Record<string, ShopperProducts.schemas['Product']>;
    productMapSignalMode?: 'include_message_flags' | 'strict_unavailable_only';
    deliveryZoneStatus?: 'mapped' | 'unmapped' | 'unknown';
    disabledTerritory?: { prefix: string; territory: string } | null;
}): boolean {
    const {
        basket,
        merchandiseItems,
        productsByItemIdForAvailability,
        productMapSignalMode = 'include_message_flags',
        deliveryZoneStatus,
        disabledTerritory,
    } = params;
    if (!basket) {
        return Boolean(disabledTerritory);
    }
    const basketAtpStatus = (basket as { c_atpStatus?: 'enriched' | 'no_zip' | 'error' | null }).c_atpStatus;
    const hasUnavailableItems = merchandiseItems.some((item) => {
        const productDetails =
            (item.itemId && productsByItemIdForAvailability?.[item.itemId]) ||
            (item.productId && productsByItemIdForAvailability?.[item.productId]) ||
            undefined;
        const isUnavailableFromProductMap =
            productMapSignalMode === 'strict_unavailable_only'
                ? isHardUnavailableCartItem(productDetails as Record<string, unknown> | undefined)
                : isUnavailableCartItem(productDetails as Record<string, unknown> | undefined);
        return isCheckoutBlockedCartItem(item, basketAtpStatus) || isUnavailableFromProductMap;
    });
    const hasHomeDelivery = basketHasHomeDeliveryItem(basket);
    const basketShippingUnavailable = (basket as { c_shippingUnavailable?: boolean }).c_shippingUnavailable === true;
    const isOutOfRegion = (deliveryZoneStatus === 'unmapped' && hasHomeDelivery) || basketShippingUnavailable;
    return hasUnavailableItems || isOutOfRegion || Boolean(disabledTerritory);
}
